import React, { useRef, useCallback, useEffect, useContext } from 'react';
import proptypes from 'prop-types';
import { QueryContext, QueryProvider, useDataModel, extend } from '@thd-nucleus/data-sources';
import { useStore } from '@thd-nucleus/experience-context';
import { ImpressionProvider } from '@thd-olt-component-react/impression';
import { DiscoveryZoneCarousel } from './DiscoveryZoneCarousel';
import DynamicRecs from '../dynamic-recs/DynamicRecs';
import SearchViewedRecs from '../search-viewed/SearchViewedRecs';
import { discoveryZonesModel as dataModel } from '../dataModel';
import { impressionContainerNameParser } from '../utils/helpers';

const {
  string: stringType, object, shape, bool, arrayOf
} = proptypes;

const RECS_REQUEST_KEY = 'aGAQFG4j6QtVTSWqujfFYWeIU6BR5Mee';
const DISCOVERY_ZONES_COMPONENT_NAME = 'DiscoveryZones';
const ANALYTICS_REC_TYPE = 'DiscoveryZone';

export const DiscoveryZones = (props) => {

  const {
    pageType,
    schemaName,
    keyword,
    zoneId,
    nValue,
    itemIds,
    storeId: searchStoreId,
    data: dataProp,
    loading: loadingProp
  } = props;

  const { defaultVariables } = useContext(QueryContext) || {};
  const didFallbackRender = useRef({ fallbackRendered: false, zoneId });
  const listenerSet = useRef(false);
  const fallbackEventHandler = useCallback(({ output: { fallbackRendered, renderedZoneId } }) => {
    didFallbackRender.current = { fallbackRendered, zoneId: renderedZoneId };
  }, []);

  if (typeof window !== 'undefined' && typeof LIFE_CYCLE_EVENT_BUS !== 'undefined' && !listenerSet.current) {
    LIFE_CYCLE_EVENT_BUS.lifeCycle.on('discovery-zones.fallback', fallbackEventHandler);
    listenerSet.current = true;
  }

  useEffect(() => {
    return () => LIFE_CYCLE_EVENT_BUS.lifeCycle.off('discovery-zones.fallback', fallbackEventHandler);
  }, []);

  const titleMaxLine = 3;
  let searchModel = null;
  let zoneData = {};
  let subtitle = null;
  let sponsoredTrue = false;

  const { storeId, storeZip: zipCode, isLocalized, membershipInformation } = useStore();

  // { data, error, loading, variables }
  const searchModelResponse = useDataModel('searchModel', {
    variables: {
      navParam: nValue,
      storeId: searchStoreId || storeId,
      dataSource: schemaName
    },
    skip: !isLocalized || dataProp || loadingProp,
    ssr: false
  });

  const data = dataProp || searchModelResponse.data;
  const loading = loadingProp || searchModelResponse.loading;

  if (data) {
    searchModel = data.searchModel;
    zoneData = searchModel?.discoveryZones
      ?.find((discoveryZone) => discoveryZone.metadata.zone === zoneId);

    sponsoredTrue = zoneData?.metadata?.zoneSponsored;
    subtitle = sponsoredTrue ? 'Sponsored' : null;
  }

  const mountedFn = ({ mounted }) => {
    if (!isLocalized) {
      return false;
    }
    if (loading) {
      return false;
    }
    return mounted;
  };

  const items = itemIds?.length > 0 ? itemIds.toString() : '';

  if (pageType === 'search' && !zoneData && didFallbackRender.current.zoneId === zoneId) {
    if (
      !didFallbackRender.current.fallbackRendered && typeof window !== 'undefined'
      && typeof LIFE_CYCLE_EVENT_BUS !== 'undefined'
    ) {
      LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger(
        'discovery-zones.fallback', { fallbackRendered: true, renderedZoneId: zoneId }
      );
    }
    
    return (
      <QueryProvider cacheKey="search-viewed-plp" mounted={mountedFn} defaultVariables={defaultVariables.current}>
        <SearchViewedRecs
          anchorId={keyword}
          requestKey={RECS_REQUEST_KEY}
          parent="discovery-zones-search-viewed"
          showLoading
          errorBoundary
          itemIds={items}
          zonePosition={zoneId}
          // what needs to be passed to SearchViewedRecs?
          impressionData={{
            id: '',
            component: DISCOVERY_ZONES_COMPONENT_NAME,
            zonePosition: zoneId,
            type: 'product',
            recType: ANALYTICS_REC_TYPE
          }}
        />
      </QueryProvider>
    );
  }

  if (!zoneData) {
    return null;
  }

  if (
    !zoneData?.products?.length
    && zoneData?.metadata?.zoneTitle?.includes('Trending')
    && isLocalized
  ) {
    return (
      <QueryProvider cacheKey="trending-products-plp" mounted={mountedFn} defaultVariables={defaultVariables.current}>
        <DynamicRecs
          anchorId={`N-${nValue}`}
          hideATC
          hideSwatches
          hideFavorites
          requestKey={RECS_REQUEST_KEY}
          schemaName="trending_products"
          parent="discovery-zones-trendingproducts"
          zoneName={zoneData?.metadata?.zoneTitle}
          zonePosition={zoneData?.metadata?.zone}
          checkMinimumProducts
          brandTitleMaxLine={titleMaxLine}
          impressionData={{
            recType: ANALYTICS_REC_TYPE
          }}
        />
      </QueryProvider>
    );
  }

  const anchorIdForAnalytics = `N-${nValue}`;
  const itemId = zoneData?.products?.[0]?.itemId; // Extract itemId from the first product if available

  return (
    <div
      id="discoveryZones"
      data-type="container"
      data-component={`discoveryZones-${zoneId}`}
      className="sui-pb-0 sui-pt-0 sui-mb-5 sui-p-4 sui-w-full sui-bg-primary"
    >
      <meta data-prop="name" content="discoveryZones" />
      <ImpressionProvider
        data={{
          id: '',
          component: DISCOVERY_ZONES_COMPONENT_NAME,
          name: impressionContainerNameParser(zoneData?.metadata?.zoneTitle),
          zonePosition: zoneData?.metadata?.zone,
          type: sponsoredTrue ? 'sponsoredproduct' : 'product',
          recType: ANALYTICS_REC_TYPE
        }}
      >
        <DiscoveryZoneCarousel
          itemClass="fixed-item"
          title={zoneData?.metadata?.zoneTitle}
          subtitle={subtitle}
          zoneSponsored={sponsoredTrue}
          scheme={schemaName}
          membershipInformation={membershipInformation}
          zipCode={zipCode}
          analyticsAnchorProductSku={anchorIdForAnalytics}
          brandTitleMaxLine={titleMaxLine}
          itemId={itemId}
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...props}
          // do not move this below the data
          storeId={searchStoreId || storeId}
          data={zoneData}
          loading={loading || !isLocalized}
          mountedFn={mountedFn}

        />
      </ImpressionProvider>
    </div>
  );
};

DiscoveryZones.displayName = DISCOVERY_ZONES_COMPONENT_NAME;
DiscoveryZones.dataModel = extend({}, dataModel, DiscoveryZoneCarousel);
DiscoveryZones.propTypes = {
  schemaName: stringType.isRequired,
  pageType: stringType,
  storeId: stringType,
  zoneId: stringType.isRequired,
  keyword: stringType,
  // eslint-disable-next-line react/forbid-prop-types
  searchModelData: object,
  nValue: stringType,
  data: shape({}),
  loading: bool,
  itemIds: arrayOf(stringType)
};

DiscoveryZones.defaultProps = {
  pageType: '',
  keyword: '',
  nValue: '',
  searchModelData: null,
  storeId: '',
  data: null,
  loading: false,
  itemIds: null
};
